import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { BRAND, SHOWCASE } from "@/config/brand";

const C = BRAND.colors;
const teal = "#14B8A6";
const gold = "#FFD700";

// Slides shown inside the TV. Each slide is a full-bleed image with
// meta metadata used for the caption card below the TV.
// `fit` overrides object-fit (default "cover"); `bg` sets the frame background
// behind a "contain"-fit image so text-heavy infographics don't get cropped.
const SLIDES = [
  {
    id: "offer-299",
    offer: true,
    channel: "LIMITED OFFER · LIVE",
    channelColor: gold,
    kicker: "SUMMER LAUNCH · WHILE SLOTS LAST",
    title: "$299.99 gets you a 50-page website.",
    subtitle: "Full SEO, mobile-perfect, AI chat included. Ask for details — we'll tailor the pages to your business the same week.",
    href: "tel:+12148155966",
  },
  {
    id: "seo-google-update-2026",
    src: "/kit-seo-google-update.png",
    fit: "contain",
    bg: "#F6F7FB",
    channel: "SEO INTEL · MARCH 2026",
    channelColor: "#22C55E",
    kicker: "VOTED TOP-10 SEO EXPERT · WORLDWIDE · 2026",
    title: "Kit lives and breathes SEO + Google Analytics.",
    subtitle: "Voted Top 10 in the world — again. Here's Kit's breakdown of Google's March 2026 Core Update and what it means for your rankings.",
    href: "/local-seo-dallas",
  },
  {
    id: "cnet",
    src: "/kit-cnet-feature.png",
    channel: "CONCEPT · PRESS PIECE",
    channelColor: teal,
    kicker: "STUDIO CONCEPT · NOT AIRED",
    title: "How Kit talks about search on-camera.",
    subtitle:
      "A studio-style concept piece we mocked up while press outreach is in progress. The words are Kit's; the network chyron is a placeholder.",
    href: null,
  },
  ...SHOWCASE.map((s) => ({
    id: `client-${s.slug}`,
    src: s.screenshotDesktop,
    channel: "CLIENT WORK",
    channelColor: teal,
    kicker: s.location,
    title: s.name,
    subtitle: s.industry,
    href: s.url,
  })),
];

const AUTOPLAY_MS = 6500;

// Client-side "slots left this month" counter — decreases as the month
// progresses so the offer feels genuinely time-boxed. Cadence:
//   day 1  → 12 slots · day 15 → ~7 · day 30 → ~2 (min 2)
function slotsLeftThisMonth() {
  const now = new Date();
  const day = now.getDate();
  const maxDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const start = 12;
  const min = 2;
  const progress = Math.min(1, day / maxDay);
  return Math.max(min, Math.round(start - progress * (start - min)));
}

function monthLabel() {
  return new Date().toLocaleString(undefined, { month: "long" });
}

export default function KitOnTV({ compact = false }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(Date.now());

  const slide = SLIDES[idx];

  const goto = (i) => {
    setIdx(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);
    startRef.current = Date.now();
    setProgress(0);
  };
  const next = () => goto(idx + 1);
  const prev = () => goto(idx - 1);

  // Auto-advance with progress bar
  useEffect(() => {
    if (paused) return undefined;
    startRef.current = Date.now();
    setProgress(0);
    timerRef.current = setTimeout(next, AUTOPLAY_MS);
    const tick = () => {
      const t = Date.now() - startRef.current;
      setProgress(Math.min(1, t / AUTOPLAY_MS));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      clearTimeout(timerRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [idx, paused]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);

  const time = useMemo(() => {
    const d = new Date();
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }, [idx]);

  const wrapperPy = compact ? "py-14 md:py-20" : "py-24 md:py-32";

  return (
    <section
      className={`relative z-10 ${wrapperPy} overflow-hidden`}
      data-testid="kit-on-tv"
      style={{ background: "transparent" }}
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(900px 500px at 50% 40%, ${teal}22, transparent 60%),
                       radial-gradient(700px 400px at 20% 80%, ${gold}12, transparent 70%),
                       radial-gradient(700px 400px at 80% 80%, #3B82F622, transparent 70%)`,
        }}
      />

      <div className="mx-auto max-w-[1180px] px-6 md:px-10 relative">
        {/* Section kicker */}
        {!compact && (
          <div className="text-center mb-8 md:mb-12">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.24em]"
              style={{
                background: `${teal}18`,
                color: teal,
                border: `1px solid ${teal}66`,
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: teal, boxShadow: `0 0 8px ${teal}` }}
              />
              Work reel · Kit &amp; the team of 4
            </div>
            <h2
              className="mt-4 font-black tracking-[-0.02em] leading-[1.02]"
              style={{ fontSize: "clamp(2rem, 4.2vw, 3.4rem)", color: "white" }}
            >
              Recent work &{" "}
              <span
                style={{
                  backgroundImage: `linear-gradient(135deg, ${teal} 0%, ${gold} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                a concept press piece
              </span>
              .
            </h2>
            <p
              className="mt-3 max-w-[620px] mx-auto text-sm md:text-base"
              style={{ color: C.textDim }}
            >
              Live client sites we&apos;ve personally built this year — plus one studio-style concept video from a press piece we&apos;re working on. Click the arrows or the dots to flip through.
            </p>
          </div>
        )}

        {/* TV set */}
        <div
          className="relative mx-auto"
          style={{ maxWidth: 1080 }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="tv-set"
        >
          {/* Floor glow shadow */}
          <div
            aria-hidden
            className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
            style={{
              bottom: -60,
              width: "78%",
              height: 120,
              background: `radial-gradient(closest-side, ${teal}55, transparent 70%)`,
              filter: "blur(28px)",
              opacity: 0.7,
            }}
          />

          {/* Bezel */}
          <div
            className="relative rounded-[28px] md:rounded-[36px] p-2.5 md:p-4"
            style={{
              background:
                "linear-gradient(180deg, #1a1f2b 0%, #0a0d14 40%, #05070c 100%)",
              boxShadow:
                "0 60px 160px -40px rgba(20,184,166,0.35), 0 40px 90px -30px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.6)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            {/* Metallic top highlight */}
            <div
              aria-hidden
              className="absolute left-6 right-6 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
              }}
            />

            {/* Inner screen frame */}
            <div
              className="relative rounded-[18px] md:rounded-[24px] overflow-hidden"
              style={{
                background: "#000",
                boxShadow:
                  "inset 0 0 0 2px rgba(0,0,0,0.9), inset 0 0 24px rgba(0,0,0,0.85)",
                aspectRatio: "16 / 10",
              }}
            >
              {/* Slides */}
              {SLIDES.map((s, i) => (
                <div
                  key={s.id}
                  className="absolute inset-0 transition-opacity duration-700"
                  style={{ opacity: i === idx ? 1 : 0, zIndex: i === idx ? 2 : 1 }}
                  aria-hidden={i !== idx}
                >
                  {s.offer ? (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center px-8 md:px-14 text-center"
                      style={{
                        background:
                          "radial-gradient(ellipse at center, rgba(255,215,0,0.14) 0%, rgba(0,0,0,0.92) 60%, #000 100%)",
                        color: "#FFFFFF",
                      }}
                      data-testid={`tv-slide-${s.id}`}
                    >
                      <div
                        className="uppercase font-black tracking-[0.34em] mb-4 md:mb-6"
                        style={{ color: gold, fontSize: "clamp(9px, 1.1vw, 12px)" }}
                      >
                        Limited launch offer
                      </div>
                      <div
                        className="font-black leading-[0.95] tracking-[-0.02em]"
                        style={{
                          fontFamily: "'Cabinet Grotesk','Inter',system-ui,sans-serif",
                          fontSize: "clamp(2.2rem, 6vw, 5rem)",
                          textShadow: `0 0 40px rgba(255,215,0,0.35), 0 0 16px rgba(255,215,0,0.55)`,
                        }}
                      >
                        <span style={{ color: gold }}>$299</span>
                        <span style={{ color: gold, opacity: 0.75, fontSize: "0.55em", verticalAlign: "super" }}>.99</span>
                      </div>
                      <div
                        className="mt-3 md:mt-4 font-black tracking-tight"
                        style={{ fontSize: "clamp(1rem, 2.2vw, 1.8rem)", color: "#FFFFFF" }}
                      >
                        gets you a <span style={{ color: gold }}>50-page website.</span>
                      </div>
                      <div
                        className="mt-4 md:mt-6 max-w-[560px] leading-relaxed"
                        style={{ color: "rgba(255,255,255,0.75)", fontSize: "clamp(11px, 1.2vw, 14px)" }}
                      >
                        Full SEO · mobile-perfect · AI chat included. Same week turnaround.{" "}
                        <span className="font-black" style={{ color: gold }}>Ask for details.</span>
                      </div>
                      <div
                        className="mt-5 md:mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full"
                        style={{
                          border: `1px solid ${gold}`,
                          color: gold,
                          fontSize: 11,
                          fontWeight: 900,
                          letterSpacing: "0.24em",
                          textTransform: "uppercase",
                          background: "rgba(255,215,0,0.06)",
                          boxShadow: `0 0 30px -8px ${gold}`,
                        }}
                      >
                        Call (214) 815-5966
                      </div>

                      {/* Slots-left counter — real urgency */}
                      <div
                        className="mt-4 md:mt-6 inline-flex items-center gap-3 md:gap-4 px-4 py-2 rounded"
                        style={{
                          background: "rgba(220,38,38,0.14)",
                          border: "1px solid rgba(220,38,38,0.55)",
                          color: "#FFF",
                          boxShadow: "0 0 24px -8px rgba(220,38,38,0.75)",
                        }}
                        data-testid="tv-slots-counter"
                      >
                        <span
                          className="inline-block w-2 h-2 rounded-full animate-pulse"
                          style={{ background: "#EF4444", boxShadow: "0 0 12px #EF4444" }}
                        />
                        <span className="text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-black" style={{ color: "#FCA5A5" }}>
                          Only
                        </span>
                        <span
                          className="font-black leading-none tabular-nums"
                          style={{ fontSize: "clamp(1.4rem, 2.6vw, 2.1rem)", color: "#FFF" }}
                          data-testid="tv-slots-count"
                        >
                          {slotsLeftThisMonth()}
                        </span>
                        <span className="text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-black" style={{ color: "#FCA5A5" }}>
                          slots left in {monthLabel()}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="w-full h-full"
                      style={{ background: s.bg || "#000" }}
                    >
                      <img
                        src={s.src}
                        alt={s.title}
                        className="w-full h-full"
                        style={{ objectFit: s.fit || "cover" }}
                        loading={i === 0 ? "eager" : "lazy"}
                        data-testid={`tv-slide-${s.id}`}
                      />
                    </div>
                  )}
                </div>
              ))}

              {/* Scanline / CRT overlay */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none z-10"
                style={{
                  background:
                    "repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 3px)",
                  mixBlendMode: "overlay",
                }}
              />
              {/* Vignette */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none z-10"
                style={{
                  boxShadow:
                    "inset 0 0 120px rgba(0,0,0,0.55), inset 0 0 30px rgba(0,0,0,0.45)",
                }}
              />
              {/* Diagonal glare */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none z-10"
                style={{
                  background:
                    "linear-gradient(120deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 22%, rgba(255,255,255,0) 100%)",
                }}
              />

              {/* LIVE chip */}
              <div
                className="absolute top-3 md:top-5 left-3 md:left-5 z-20 inline-flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-md"
                style={{
                  background: teal,
                  color: "white",
                  fontSize: 10,
                  fontWeight: 900,
                  letterSpacing: "0.24em",
                  textTransform: "uppercase",
                  boxShadow: `0 6px 18px -6px ${teal}aa`,
                }}
                data-testid="tv-live-chip"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: "white" }}
                />
                Reel
              </div>

              {/* Channel chip */}
              <div
                className="absolute top-3 md:top-5 right-3 md:right-5 z-20 inline-flex items-center gap-2 px-2.5 md:px-3 py-1 md:py-1.5 rounded-md"
                style={{
                  background: "rgba(0,0,0,0.55)",
                  border: `1px solid ${slide.channelColor}88`,
                  color: "white",
                  fontSize: 10,
                  fontWeight: 900,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  backdropFilter: "blur(6px)",
                }}
                data-testid="tv-channel-chip"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: slide.channelColor,
                    boxShadow: `0 0 8px ${slide.channelColor}`,
                  }}
                />
                {slide.channel}
              </div>

              {/* Bottom ticker */}
              <div
                className="absolute left-0 right-0 bottom-0 z-20 px-3 md:px-6 py-2 md:py-3 flex items-center justify-between"
                style={{
                  background:
                    "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 100%)",
                }}
              >
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                  <span
                    className="hidden md:inline text-[10px] font-black uppercase tracking-[0.24em] px-2 py-1 rounded"
                    style={{ background: "white", color: "#000" }}
                  >
                    PlugitAI
                  </span>
                  <span
                    className="truncate text-[11px] md:text-[13px] font-bold text-white"
                    style={{ letterSpacing: "0.02em" }}
                  >
                    {slide.title}
                  </span>
                </div>
                <span
                  className="hidden sm:inline text-[10px] md:text-[11px] font-mono text-white/70 whitespace-nowrap ml-3"
                  data-testid="tv-time"
                >
                  {time} · CH {String(idx + 1).padStart(2, "0")}
                </span>
              </div>

              {/* Progress bar */}
              <div
                aria-hidden
                className="absolute left-0 right-0 bottom-0 h-[3px] z-30"
                style={{ background: "rgba(255,255,255,0.06)" }}
              >
                <div
                  className="h-full"
                  style={{
                    width: `${progress * 100}%`,
                    background: `linear-gradient(90deg, ${teal} 0%, ${gold} 100%)`,
                    boxShadow: `0 0 12px ${teal}88`,
                    transition: "width 60ms linear",
                  }}
                />
              </div>

              {/* Arrow buttons */}
              <button
                type="button"
                onClick={prev}
                aria-label="Previous slide"
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-11 md:h-11 rounded-full flex items-center justify-center text-white/90 hover:text-white transition-all"
                style={{
                  background: "rgba(0,0,0,0.55)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  backdropFilter: "blur(6px)",
                }}
                data-testid="tv-prev-btn"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next slide"
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-11 md:h-11 rounded-full flex items-center justify-center text-white/90 hover:text-white transition-all"
                style={{
                  background: "rgba(0,0,0,0.55)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  backdropFilter: "blur(6px)",
                }}
                data-testid="tv-next-btn"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Bezel logo + power LED */}
            <div className="relative flex items-center justify-center pt-2 md:pt-3">
              <span
                className="text-[9px] md:text-[10px] font-black tracking-[0.32em] uppercase"
                style={{ color: "rgba(255,255,255,0.35)" }}
              >
                PlugitAI
              </span>
              <span
                aria-hidden
                className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full"
                style={{
                  background: teal,
                  boxShadow: `0 0 8px ${teal}, 0 0 2px ${teal}`,
                }}
              />
            </div>
          </div>

          {/* Pedestal stand */}
          <div className="flex flex-col items-center" aria-hidden>
            <div
              className="w-16 md:w-24 h-4 md:h-5"
              style={{
                background:
                  "linear-gradient(180deg, #1a1f2b 0%, #0a0d14 100%)",
                borderLeft: "1px solid rgba(255,255,255,0.06)",
                borderRight: "1px solid rgba(255,255,255,0.06)",
              }}
            />
            <div
              className="w-40 md:w-64 h-2 md:h-3 rounded-b-lg"
              style={{
                background:
                  "linear-gradient(180deg, #10131b 0%, #05070c 100%)",
                boxShadow: "0 12px 30px -10px rgba(0,0,0,0.9)",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            />
          </div>

          {/* Dot indicators */}
          <div
            className="flex items-center justify-center gap-2 mt-6 md:mt-8"
            data-testid="tv-dots"
          >
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => goto(i)}
                className="transition-all"
                style={{
                  width: i === idx ? 28 : 8,
                  height: 8,
                  borderRadius: 999,
                  background:
                    i === idx
                      ? `linear-gradient(90deg, ${teal}, ${gold})`
                      : "rgba(255,255,255,0.18)",
                  boxShadow: i === idx ? `0 0 12px ${teal}88` : "none",
                }}
                data-testid={`tv-dot-${i}`}
              />
            ))}
          </div>

          {/* Caption card (below TV) */}
          <div
            className="mt-8 md:mt-10 mx-auto max-w-[820px] rounded-2xl p-5 md:p-7 border text-left"
            style={{
              background: "rgba(11,21,36,0.72)",
              borderColor: `${slide.channelColor}44`,
              backdropFilter: "blur(10px)",
              boxShadow: `0 30px 90px -40px ${slide.channelColor}55`,
            }}
            data-testid="tv-caption-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <span
                className="text-[10px] font-black uppercase tracking-[0.24em]"
                style={{ color: slide.channelColor }}
              >
                {slide.kicker}
              </span>
              <span
                className="w-1 h-1 rounded-full"
                style={{ background: C.textFade }}
              />
              <span
                className="text-[10px] uppercase tracking-[0.22em]"
                style={{ color: C.textFade }}
              >
                {String(idx + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
              </span>
            </div>
            <h3
              className="font-black text-white leading-tight"
              style={{ fontSize: "clamp(1.15rem, 2.2vw, 1.6rem)" }}
              data-testid="tv-caption-title"
            >
              {slide.title}
            </h3>
            <p
              className="mt-2 text-sm md:text-base leading-relaxed"
              style={{ color: C.textDim }}
              data-testid="tv-caption-subtitle"
            >
              {slide.subtitle}
            </p>
            {slide.href && (
              <a
                href={slide.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold"
                style={{ color: slide.channelColor }}
                data-testid="tv-caption-link"
              >
                Visit the live site <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
